import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { getSnapshotStatus, requestSnapshotRefresh } from "../lib/api";
import type { SnapshotStatusResponse } from "../types/inpact";

const POLL_INTERVAL_MS = 30_000;

export interface UseSnapshotStatusResult {
  status: SnapshotStatusResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  statusError: Error | null;
  refreshError: Error | null;
  refresh: () => Promise<void>;
}

export function useSnapshotStatus(): UseSnapshotStatusResult {
  const queryClient = useQueryClient();
  const invalidateDataQueries = useCallback(() => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["contacts"] }),
    queryClient.invalidateQueries({ queryKey: ["aggregate"] }),
    queryClient.invalidateQueries({ queryKey: ["inpact", "fields"] }),
  ]), [queryClient]);
  const query = useQuery({
    queryKey: ["inpact", "snapshot"],
    queryFn: getSnapshotStatus,
    refetchInterval: POLL_INTERVAL_MS,
  });
  const seenVersions = useRef(new Set<string>());
  const versionInitialized = useRef(false);
  useEffect(() => {
    const current = query.data?.state;
    const version = query.data ? [
      query.data.refreshed_at,
      query.data.policy_version,
      query.data.schema_fingerprint,
    ].join("|") : undefined;
    const unseenVersion = version !== undefined && !seenVersions.current.has(version);
    const completed = current && current !== "refreshing"
      && versionInitialized.current && unseenVersion;
    if (completed) {
      void invalidateDataQueries();
    }
    if (version !== undefined) {
      seenVersions.current.add(version);
      versionInitialized.current = true;
    }
  }, [invalidateDataQueries, query.data, query.dataUpdatedAt]);
  const refreshMutation = useMutation({
    mutationFn: requestSnapshotRefresh,
    onSuccess: async () => {
      await Promise.all([
        query.refetch(),
        queryClient.invalidateQueries({ queryKey: ["inpact", "fields"] }),
      ]);
    },
  });

  return {
    status: query.data ?? null,
    isLoading: query.isLoading,
    isRefreshing:
      refreshMutation.isPending || query.data?.state === "refreshing",
    statusError: query.error as Error | null,
    refreshError: refreshMutation.error as Error | null,
    refresh: async () => {
      try {
        await refreshMutation.mutateAsync();
      } catch {
        // React Query retains the error for the visible status indicator.
        // The click handler must not create an unhandled promise rejection.
      }
    },
  };
}
